import type { FC } from 'react';
import { lazy, Suspense } from 'react';

import type { WorkspaceResource } from '@/hooks/useWorkspaceResourceAccess';
import { isCommentableWorkspaceResource } from '@/shared/components/activity/resourceCommentAccess';
import LifecyclePreviewSurface from '@/shared/components/lifecycles/LifecyclePreviewSurface';

import {
    WORKSPACE_RESOURCE_PREVIEW_REGISTRY,
    type WorkspaceResourcePreviewRegistryEntry,
} from './registry';
import type { WorkspaceResourcePreviewContentProps } from './types';
import WorkspaceResourceHoverCardContent from './WorkspaceResourceHoverCardContent';

const registeredPreviewComponents = (
    Object.entries(WORKSPACE_RESOURCE_PREVIEW_REGISTRY) as Array<
        [WorkspaceResource, WorkspaceResourcePreviewRegistryEntry]
    >
).reduce<Partial<Record<WorkspaceResource, FC<WorkspaceResourcePreviewContentProps>>>>(
    (components, [resourceType, entry]) => {
        if (entry.loadPreviewComponent) {
            components[resourceType] = lazy(entry.loadPreviewComponent);
        }

        return components;
    },
    {},
);

/** Dispatches every workspace-resource preview through the shared registry. */
const WorkspaceResourcePreviewContent: FC<WorkspaceResourcePreviewContentProps> = (props) => {
    const entry = isCommentableWorkspaceResource(props.data.resourceType)
        ? WORKSPACE_RESOURCE_PREVIEW_REGISTRY[props.data.resourceType as WorkspaceResource]
        : undefined;
    const PreviewComponent =
        (entry ? registeredPreviewComponents[props.data.resourceType as WorkspaceResource] : undefined) ??
        WorkspaceResourceHoverCardContent;

    return (
        <Suspense fallback={<LifecyclePreviewSurface previewState='loading' style={{ marginTop: '0.5rem' }} />}>
            <PreviewComponent {...props} />
        </Suspense>
    );
};

export default WorkspaceResourcePreviewContent;
